/**
 * COLLECTION & FUSION — l'écran où l'enfant voit ses créatures débloquées (par salle,
 * possédées vs verrouillées) et en FUSIONNE deux pour créer une légendaire unique.
 * Thémé automatiquement (variables --kid-* → cyan pour Isaac, rose pour Bao).
 * Le wrapper fetch global ajoute le jeton + X-Child-Id, donc les appels sont simples.
 */
import { useCallback, useEffect, useState } from "react";

type Rarity = "common" | "rare" | "legendary";
type Creature = { id: string; name: string; emoji: string; rarity: Rarity; owned: boolean };
type Fusion = { id: string; name: string; emoji: string; parents: string[] };
type RoomData = { creatures: Creature[]; owned: number; total: number };
type CollectionData = {
  rooms: Record<string, RoomData>;
  fusions: Fusion[];
  ownedCount: number;
  totalCount: number;
};

const ROOM_TITLES: Record<string, string> = {
  "fairy-realm": "Royaume Féerique",
  "wild-kingdom": "Royaume Sauvage",
  "bug-lab": "Labo des Insectes",
  "dino-vault": "Chambre des Dinosaures",
  "space-dock": "Station Spatiale",
  greenhouse: "Serre",
  "robotics-bay": "Atelier Robotique",
  "world-atlas": "Atlas du Monde",
};
// Ordre d'affichage (les salles "mignonnes" en premier — agréable pour Bao, neutre sinon).
const ROOM_ORDER = [
  "fairy-realm", "wild-kingdom", "bug-lab", "dino-vault",
  "space-dock", "greenhouse", "robotics-bay", "world-atlas",
];

const rarityColor = (r: Rarity) =>
  r === "legendary" ? "#ffd43b" : r === "rare" ? "var(--kid-cyan)" : "var(--kid-text-3)";

export function CollectionTile({ onOpen }: { onOpen: () => void }) {
  return (
    <>
      <style>{TILE_CSS}</style>
      <button className="collection-tile" onClick={onOpen} aria-label="Ma collection">
        🎴 <span>COLLECTION</span>
      </button>
    </>
  );
}

export default function Collection({ apiBase, onClose }: { apiBase: string; onClose: () => void }) {
  const [data, setData] = useState<CollectionData | null>(null);
  const [fuseMode, setFuseMode] = useState(false);
  const [picks, setPicks] = useState<string[]>([]);
  const [result, setResult] = useState<{ f: Fusion; already: boolean } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${apiBase}/api/child/collection`);
      if (r.ok) setData(await r.json());
    } catch {
      /* offline — écran vide */
    }
  }, [apiBase]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleFuseMode = () => {
    setFuseMode((v) => !v);
    setPicks([]);
    setResult(null);
  };

  const tapCreature = (c: Creature) => {
    if (!fuseMode || !c.owned) return;
    setResult(null);
    setPicks((prev) =>
      prev.includes(c.id) ? prev.filter((x) => x !== c.id) : prev.length < 2 ? [...prev, c.id] : [prev[1], c.id]
    );
  };

  const doFuse = async () => {
    if (picks.length !== 2 || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${apiBase}/api/child/fuse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ a: picks[0], b: picks[1] }),
      });
      const d = await r.json();
      if (r.ok && d.fusion) {
        setResult({ f: d.fusion, already: !!d.already });
        setPicks([]);
        load();
      }
    } catch {
      /* ignore */
    }
    setBusy(false);
  };

  return (
    <div style={S.screen}>
      <style>{OVERLAY_CSS}</style>
      <div style={S.header}>
        <button style={S.close} onClick={onClose} aria-label="Fermer">
          ✕
        </button>
        <div style={S.title}>MA COLLECTION</div>
        <div style={S.count}>{data ? `${data.ownedCount} / ${data.totalCount}` : "…"}</div>
      </div>

      <button style={{ ...S.fuseToggle, ...(fuseMode ? S.fuseToggleOn : {}) }} onClick={toggleFuseMode}>
        {fuseMode ? "✕ Annuler la fusion" : "✨ Fusionner deux créatures"}
      </button>
      {fuseMode && (
        <div style={S.fuseHint}>
          Choisis <b>2 créatures</b> que tu possèdes pour créer une <b style={{ color: "#ffd43b" }}>légendaire</b> unique.
        </div>
      )}
      {fuseMode && picks.length === 2 && (
        <button style={S.fuseGo} onClick={doFuse} disabled={busy}>
          {busy ? "Fusion en cours…" : "🔮 FUSIONNER !"}
        </button>
      )}
      {result && (
        <div style={S.result}>
          <div style={{ fontSize: 46 }}>{result.f.emoji}</div>
          <div style={{ color: "#ffd43b", fontWeight: 800, fontSize: 18 }}>{result.f.name}</div>
          <div style={S.resultSub}>{result.already ? "Tu l'avais déjà créée !" : "✨ Nouvelle légendaire débloquée !"}</div>
        </div>
      )}

      <div style={S.scroll}>
        {data &&
          ROOM_ORDER.filter((rid) => data.rooms[rid]).map((rid) => {
            const room = data.rooms[rid];
            return (
              <div key={rid} style={S.room}>
                <div style={S.roomHead}>
                  <span>{ROOM_TITLES[rid] || rid}</span>
                  <span style={S.roomCount}>
                    {room.owned}/{room.total}
                  </span>
                </div>
                <div style={S.grid}>
                  {room.creatures.map((c) => {
                    const selected = picks.includes(c.id);
                    return (
                      <div
                        key={c.id}
                        onClick={() => tapCreature(c)}
                        style={{
                          ...S.cell,
                          borderColor: c.owned ? rarityColor(c.rarity) : "var(--kid-border)",
                          opacity: c.owned ? 1 : 0.4,
                          outline: selected ? "2px solid #ffd43b" : "none",
                          cursor: fuseMode && c.owned ? "pointer" : "default",
                        }}
                      >
                        <div style={{ fontSize: 30, filter: c.owned ? "none" : "grayscale(1)" }}>
                          {c.owned ? c.emoji : "❔"}
                        </div>
                        <div style={{ ...S.cellName, color: c.owned ? "var(--kid-text-2)" : "var(--kid-text-muted)" }}>
                          {c.owned ? c.name : "???"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

        {data && data.fusions.length > 0 && (
          <div style={S.room}>
            <div style={S.roomHead}>
              <span style={{ color: "#ffd43b" }}>✨ FUSIONS LÉGENDAIRES</span>
              <span style={S.roomCount}>{data.fusions.length}</span>
            </div>
            <div style={S.grid}>
              {data.fusions.map((f) => (
                <div key={f.id} style={{ ...S.cell, borderColor: "#ffd43b" }}>
                  <div style={{ fontSize: 26 }}>{f.emoji}</div>
                  <div style={{ ...S.cellName, color: "#ffe08a" }}>{f.name}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ height: 40 }} />
      </div>
    </div>
  );
}

const TILE_CSS = `
.collection-tile { position: absolute; bottom: calc(max(14px, env(safe-area-inset-bottom)) + 46px); left: 14px; z-index: 25; display: flex; align-items: center; gap: 7px; background: rgba(6,9,15,0.72); border: 1px solid var(--kid-cyan-40); color: var(--kid-cyan); font-family: var(--kid-font-mono); font-size: 12px; letter-spacing: 0.16em; padding: 10px 15px; border-radius: 999px; cursor: pointer; }
.collection-tile:active { transform: scale(0.96); }
`;
const OVERLAY_CSS = `
@keyframes coll-pop { from { transform: scale(0.7); opacity: 0; } to { transform: scale(1); opacity: 1; } }
`;

const S: Record<string, React.CSSProperties> = {
  screen: {
    position: "fixed",
    inset: 0,
    zIndex: 60,
    background: "var(--kid-bg)",
    display: "flex",
    flexDirection: "column",
    fontFamily: "var(--kid-font-sans, system-ui)",
    color: "var(--kid-text)",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "16px 16px 8px",
    borderBottom: "1px solid var(--kid-border)",
  },
  close: {
    width: 40,
    height: 40,
    borderRadius: 12,
    border: "1px solid var(--kid-cyan-40)",
    background: "transparent",
    color: "var(--kid-cyan)",
    fontSize: 18,
    cursor: "pointer",
  },
  title: { flex: 1, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.18em", fontSize: 16, color: "var(--kid-cyan)" },
  count: { fontFamily: "var(--kid-font-mono)", fontSize: 15, color: "var(--kid-text-2)" },
  fuseToggle: {
    margin: "12px 16px 0",
    padding: "12px",
    borderRadius: 14,
    border: "1px solid var(--kid-cyan-40)",
    background: "transparent",
    color: "var(--kid-text)",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  fuseToggleOn: { borderColor: "#ffd43b", color: "#ffd43b" },
  fuseHint: { margin: "8px 18px 0", fontSize: 13, color: "var(--kid-text-3)", lineHeight: 1.5 },
  fuseGo: {
    margin: "10px 16px 0",
    padding: "14px",
    borderRadius: 14,
    border: "none",
    background: "linear-gradient(180deg, #ffd43b, #f0a929)",
    color: "#2a1d00",
    fontSize: 17,
    fontWeight: 800,
    cursor: "pointer",
  },
  result: {
    margin: "12px 16px 0",
    padding: "14px",
    borderRadius: 16,
    border: "1px solid #ffd43b",
    background: "rgba(255,212,59,0.08)",
    textAlign: "center",
    animation: "coll-pop 0.35s ease",
  },
  resultSub: { color: "var(--kid-text-2)", fontSize: 13, marginTop: 4 },
  scroll: { flex: 1, overflowY: "auto", padding: "8px 16px 0" },
  room: { marginTop: 18 },
  roomHead: {
    display: "flex",
    justifyContent: "space-between",
    fontFamily: "var(--kid-font-mono)",
    letterSpacing: "0.1em",
    fontSize: 13,
    color: "var(--kid-text-2)",
    marginBottom: 8,
    textTransform: "uppercase",
  },
  roomCount: { color: "var(--kid-text-3)" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(84px, 1fr))", gap: 8 },
  cell: {
    aspectRatio: "1 / 1",
    borderRadius: 14,
    border: "1.5px solid var(--kid-border)",
    background: "rgba(255,255,255,0.03)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    padding: 4,
    textAlign: "center",
  },
  cellName: { fontSize: 10.5, lineHeight: 1.15, fontWeight: 600 },
};
